import { useState } from 'react';
import esLocale from 'date-fns/locale/es';

// Material UI
import { Grid, Chip, Icon, Typography, Button, InputAdornment } from '@mui/material';
import { MobileTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

// Componentes
import BasicSelect from '@components/Selects/BasicSelect';
import AdvancedSelect from '@components/Selects/AdvancedSelect';
import TextFieldTK from '@components/Form/TextFieldTK';

// Utils
import { getFormikUtil } from '@utils/Utilities';

// Data
import { SI_NO, DIAS_SEMANA } from '@data/constants/catalogs';

const FormationForm = (props) => {
  const { isLoading, formik, showChip = true } = props;

  const [topic, setTopic] = useState('');

  const getFormik = (value) => getFormikUtil(formik, 'formation', value);

  const handleDays = (value) => {
    const name = 'formation.days';

    if (value.includes(0)) {
      formik.handleChange({ target: { name, value: [1, 2, 3, 4, 5, 6, 7] } });
    } else if (value.includes(99)) {
      formik.handleChange({ target: { name, value: [1, 2, 3, 4, 5] } });
    } else if (value.includes(98)) {
      formik.handleChange({ target: { name, value: [6, 7] } });
    } else {
      formik.handleChange({ target: { name, value } });
    }
  };

  const handleAddTopic = () => {
    const prevTopics = getFormik('topics')?.value;

    if (topic && !prevTopics?.includes(topic)) {
      formik.setValues({
        ...formik.values,
        formation: { ...formik.values.formation, topics: [...prevTopics, topic] },
      });
      setTopic('');
    }
  };

  const handleRemoveTopic = (topicToRemove) => {
    const topics = getFormik('topics')?.value;
    const newTopics = topics.filter((topic) => topic !== topicToRemove);
    formik.handleChange({ target: { name: 'formation.topics', value: newTopics } });
  };

  const isYES = getFormik('idParticipate')?.value === 1;

  return (
    <Grid container spacing={3}>
      {showChip && (
        <Grid item xs={12}>
          <Chip icon={<Icon>interests</Icon>} label="Extras" />
        </Grid>
      )}

      <Grid item xs={12}>
        <TextFieldTK
          fullWidth
          multiline
          minRows={2}
          label="¿Cómo te enteraste del programa?"
          name="formation.foundBy"
          value={getFormik('foundBy').value}
          onChange={formik.handleChange}
          disabled={isLoading}
          error={getFormik('foundBy').error}
          helperText={getFormik('foundBy').helperText}
          useTrim
        />
      </Grid>

      <Grid item xs={12}>
        <TextFieldTK
          fullWidth
          name="formation.topics"
          label="¿Qué otros temas de capacitación te gustarían fueran ofertados?"
          value={topic}
          onChange={({ target: { value } }) => setTopic(value)}
          disabled={isLoading}
          error={getFormik('topics')?.error}
          helperText={getFormik('topics')?.helperText}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddTopic}
                  disabled={isLoading || topic?.trim()?.length < 5}
                >
                  Agregar
                </Button>
              </InputAdornment>
            ),
          }}
          useTrim
        />
      </Grid>

      {getFormik('topics')?.value?.length > 0 && (
        <Grid item xs={12} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {getFormik('topics')?.value?.map((topic, index) => (
            <Chip key={index} label={topic} onDelete={() => handleRemoveTopic(topic)} color="primary" />
          ))}
        </Grid>
      )}

      <Grid item xs={12} sm={12}>
        <Typography variant="body">
          ¿Qué días y horarios se te facilitan más para tomar capacitaciones?
        </Typography>
      </Grid>

      <Grid item xs={12} md={4}>
        <AdvancedSelect
          required
          name="days"
          label="Dias de la semana *"
          placeholder="Selecciona los dias"
          value={getFormik('days')?.value}
          options={DIAS_SEMANA('extras')}
          disabled={isLoading}
          error={getFormik('days')?.error}
          helperText={getFormik('days')?.helperText}
          onChange={handleDays}
          multiple
          disableCloseOnSelect
        />
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
          <MobileTimePicker
            label="Hora Inicial *"
            value={getFormik('initialDate')?.value || new Date(0, 0, 0, 0, 0)}
            onChange={(value) => {
              formik.setValues({
                ...formik.values,
                formation: { ...formik.values.formation, initialDate: value, finalDate: value },
              });
            }}
            ampm
            minutesStep={15}
            disabled={isLoading}
            renderInput={(params) => (
              <TextFieldTK
                {...params}
                fullWidth
                disabled={isLoading}
                error={getFormik('initialDate')?.error}
                helperText={getFormik('initialDate')?.helperText}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} sm={6} md={4}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={esLocale}>
          <MobileTimePicker
            label="Hora Final *"
            value={getFormik('finalDate')?.value || new Date(0, 0, 0, 0, 0)}
            onChange={(value) => {
              formik.handleChange({ target: { name: 'formation.finalDate', value } });
            }}
            ampm
            minutesStep={15}
            minTime={getFormik('initialDate')?.value || new Date(0, 0, 0, 0, 0)}
            disabled={isLoading}
            renderInput={(params) => (
              <TextFieldTK
                {...params}
                fullWidth
                disabled={isLoading}
                error={getFormik('finalDate')?.error}
                helperText={getFormik('finalDate')?.helperText}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>

      <Grid item xs={12} md={7} lg={6}>
        <BasicSelect
          size="small"
          name="formation.idParticipate"
          label="¿Participa en alguna asociación civil o proyecto de impacto social?"
          options={SI_NO}
          value={getFormik('idParticipate')?.value}
          onChange={formik.handleChange}
          isLoading={isLoading}
          error={getFormik('idParticipate').error}
          errorMessage={getFormik('idParticipate').helperText}
          sx={{ width: '100%' }}
        />
      </Grid>

      {isYES && (
        <>
          <Grid item xs={12} md={5} lg={6}>
            <TextFieldTK
              required
              fullWidth
              name="formation.participateProyect"
              label="¿En cuál?"
              placeholder="Escribe en cuál"
              value={getFormik('participateProyect')?.value}
              onChange={formik.handleChange}
              disabled={isLoading}
              error={getFormik('participateProyect')?.error}
              helperText={getFormik('participateProyect')?.helperText}
              useTrim
            />
          </Grid>

          <Grid item xs={12}>
            <TextFieldTK
              required
              fullWidth
              multiline
              minRows={2}
              name="formation.participateDescription"
              label="Describa brevemente su cargo y proyectos sociales en los que participa:"
              placeholder="Describa brevemente su cargo y proyectos sociales"
              value={getFormik('participateDescription')?.value}
              onChange={formik.handleChange}
              disabled={isLoading}
              error={getFormik('participateDescription')?.error}
              helperText={getFormik('participateDescription')?.helperText}
              useTrim
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default FormationForm;
